import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileSpreadsheet, FileText, Loader2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const defaultMonth = () => new Date().toISOString().slice(0, 7);

export default function EtatsServicePage() {
  const [month, setMonth] = useState(defaultMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async (m) => {
    try {
      const { data } = await api.get("/service-export/teachers", { params: { month: m } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(month); }, [month]);

  const dl = async (url, filename, key) => {
    setBusy(key);
    try {
      const resp = await api.get(url, { responseType: "blob", timeout: 120000 });
      const u = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = u; a.download = filename; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'export"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="etats-service-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><ClipboardList className="h-3 w-3" /><span>RH & PAIE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">États de service enseignants</h1>
          <p className="text-slate-500 mt-1">Heures de cours validées par le pointage par séance — export mensuel pour la paie.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" data-testid="service-month-input" />
          <Button onClick={() => dl(`/service-export/csv?month=${month}`, `etats_service_${month}.csv`, "csv")}
            disabled={!!busy} className="bg-[#166534] text-white gap-1" data-testid="service-export-csv">
            {busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}Export Excel global
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b bg-slate-50 font-heading font-bold text-[#1E3A8A]">
          {data ? `${data.month_label} — ${data.teachers.length} enseignant(s)` : "Chargement..."}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="service-teachers-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Enseignant</th>
                <th className="px-4 py-2 text-right">Séances</th>
                <th className="px-4 py-2 text-right">H. planifiées</th>
                <th className="px-4 py-2 text-right">H. validées</th>
                <th className="px-4 py-2 text-right">Réalisation</th>
                <th className="px-4 py-2 text-right">Retards</th>
                <th className="px-4 py-2 text-right">État de service</th>
              </tr>
            </thead>
            <tbody>
              {(data?.teachers || []).map(t => (
                <tr key={t.teacher_id} className="border-t hover:bg-slate-50" data-testid={`service-row-${t.teacher_id}`}>
                  <td className="px-4 py-2 font-semibold">{t.teacher_name || "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{t.sessions}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{t.planned_hours} h</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-[#1E3A8A]">{t.validated_hours} h</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-semibold ${t.completion_rate >= 80 ? "text-[#166534]" : t.sessions ? "text-[#EA580C]" : "text-slate-400"}`}>{t.completion_rate}%</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${t.retards > 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>{t.retards}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" className="gap-1 border-[#1E3A8A] text-[#1E3A8A]"
                      disabled={!!busy}
                      onClick={() => dl(`/service-export/pdf?teacher_id=${t.teacher_id}&month=${month}`, `etat_service_${month}.pdf`, t.teacher_id)}
                      data-testid={`service-pdf-${t.teacher_id}`}>
                      {busy === t.teacher_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}PDF
                    </Button>
                  </td>
                </tr>
              ))}
              {data && data.teachers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Aucun enseignant.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
